import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminStats, useAdminTimeline, type StatsPeriod } from '../../hooks/useAdminStats'
import { useDeliveries } from '../../hooks/useDeliveries'
import { NotificationBell } from '../../components/shared/NotificationBell'
import KpiCard from '../../components/ui/KpiCard'
import LineChart from '../../components/ui/LineChart'
import type { GlobalStats } from '../../api/analytics'
import type { AdminTimelinePoint } from '../../api/analytics'
import type { Delivery } from '../../types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const PERIOD_LABELS: Record<StatsPeriod, string> = {
  week: '7 derniers jours',
  month: '30 derniers jours',
  year: 'Cette année',
}

const fmt = (d: string) =>
  new Date(d).toLocaleDateString('fr-TN', { day: '2-digit', month: 'short' })

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('fr-TN', { day: '2-digit', month: 'short', year: 'numeric' })

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente', validated: 'Validé', accepted: 'Accepté',
  picked_up: 'Récupéré', in_transit: 'En route', delivered: 'Livré',
  failed: 'Échoué', cancelled: 'Annulé', unreachable: 'Injoignable',
}

const STATUS_COLOR: Record<string, string> = {
  delivered: '#10B981', pending: '#F59E0B', in_transit: '#3B82F6',
  failed: '#EF4444', cancelled: '#9CA3AF', validated: '#8B5CF6',
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SkeletonBlock({ height }: { height: number }) {
  return (
    <div style={{
      height, borderRadius: 10, background: '#f3f4f6',
      animation: 'pulse 1.5s ease-in-out infinite',
    }} />
  )
}

function AvatarBadge({ name }: { name: string }) {
  const initials = name.trim().slice(0, 2).toUpperCase()
  return (
    <div style={{
      width: 36, height: 36, borderRadius: '50%',
      background: '#E1F5EE', color: '#0F6E56',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: 13, flexShrink: 0,
    }}>
      {initials}
    </div>
  )
}

function PeriodSelector({ value, onChange }: { value: StatsPeriod; onChange: (p: StatsPeriod) => void }) {
  return (
    <div style={{ display: 'flex', gap: 4, background: '#F9FAFB', borderRadius: 10, padding: 4, border: '1px solid #E5E7EB' }}>
      {(['week', 'month', 'year'] as StatsPeriod[]).map((p) => (
        <button
          key={p}
          onClick={() => onChange(p)}
          style={{
            padding: '5px 12px', borderRadius: 7, border: 'none', cursor: 'pointer',
            fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
            background: value === p ? '#fff' : 'transparent',
            color: value === p ? '#0D1117' : '#9CA3AF',
            boxShadow: value === p ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}
        >
          {p === 'week' ? '7j' : p === 'month' ? '30j' : '1an'}
        </button>
      ))}
    </div>
  )
}

function DeliveryTimelineChart({ data, loading }: { data: AdminTimelinePoint[]; loading: boolean }) {
  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 12, padding: '20px 24px',
    border: '1px solid #f3f4f6',
  }
  const titleStyle: React.CSSProperties = {
    fontSize: 14, fontWeight: 600, color: '#0D1117', marginBottom: 16,
  }

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={titleStyle}>Évolution des livraisons</div>
        <SkeletonBlock height={220} />
      </div>
    )
  }

  if (!data.length) {
    return (
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
        <span style={{ color: '#9CA3AF', fontSize: 13 }}>Aucune donnée pour cette période</span>
      </div>
    )
  }

  const labels = data.map((p) => p._id.slice(5))
  const series = [
    { data: data.map((p) => p.total) },
    { data: data.map((p) => p.delivered) },
    { data: data.map((p) => p.failed) },
  ]

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={titleStyle}>Évolution des livraisons</div>
        <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#6B7280' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#0F2A4A', display: 'inline-block' }} /> Total
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#7CB342', display: 'inline-block' }} /> Livrés
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#EF4444', display: 'inline-block' }} /> Échecs
          </span>
        </div>
      </div>
      <LineChart series={series} labels={labels} />
    </div>
  )
}

function StatusDistribution({ stats, loading }: { stats?: GlobalStats['deliveries']; loading: boolean }) {
  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 12, padding: '20px 24px',
    border: '1px solid #f3f4f6',
  }

  if (loading) {
    return (
      <div style={cardStyle}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#0D1117', marginBottom: 16 }}>Répartition statuts</div>
        <SkeletonBlock height={200} />
      </div>
    )
  }

  const total = stats?.total ?? 0
  const items = [
    { label: 'Livrés', value: stats?.delivered ?? 0, color: '#10B981' },
    { label: 'En transit', value: stats?.inTransit ?? 0, color: '#3B82F6' },
    { label: 'En attente', value: stats?.pending ?? 0, color: '#F59E0B' },
    { label: 'Échecs', value: stats?.failed ?? 0, color: '#EF4444' },
    { label: 'Annulés', value: stats?.cancelled ?? 0, color: '#9CA3AF' },
  ]

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#0D1117', marginBottom: 4 }}>Répartition statuts</div>
      <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 16 }}>{total} colis total</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {items.map(({ label, value, color }) => {
          const pct = total > 0 ? Math.round((value / total) * 100) : 0
          return (
            <div key={label}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, fontSize: 13 }}>
                <span style={{ color: '#374151', fontWeight: 500 }}>{label}</span>
                <span style={{ color: '#6B7280' }}>
                  <b style={{ color: '#0D1117' }}>{value}</b> · {pct}%
                </span>
              </div>
              <div style={{ height: 6, borderRadius: 3, background: '#F3F4F6', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color, borderRadius: 3, transition: 'width 0.4s' }} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TopPerformersCard({ stats, loading }: { stats?: GlobalStats; loading: boolean }) {
  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 12, padding: '20px 24px',
    border: '1px solid #f3f4f6',
  }

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#0D1117', marginBottom: 16 }}>Top Performers</div>
      {loading ? <SkeletonBlock height={140} /> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Meilleur livreur
            </div>
            {stats?.livreurs.topPerformer ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <AvatarBadge name={stats.livreurs.topPerformer.name} />
                  <span style={{ fontWeight: 600, color: '#0D1117', fontSize: 13 }}>
                    {stats.livreurs.topPerformer.name}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: '#1D9E75', fontSize: 13 }}>
                    {stats.livreurs.topPerformer.deliveries} livraisons
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {stats.livreurs.topPerformer.revenue} TND
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ color: '#9CA3AF', fontSize: 13 }}>Aucune donnée</div>
            )}
          </div>

          <div style={{ borderTop: '1px solid #F3F4F6' }} />

          <div>
            <div style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Expéditeur le plus actif
            </div>
            {stats?.expediteurs.topSender ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <AvatarBadge name={stats.expediteurs.topSender.name} />
                  <span style={{ fontWeight: 600, color: '#0D1117', fontSize: 13 }}>
                    {stats.expediteurs.topSender.name}
                  </span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: '#F59E0B', fontSize: 13 }}>
                    {stats.expediteurs.topSender.parcels} colis
                  </div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                    {stats.expediteurs.topSender.spent} TND
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ color: '#9CA3AF', fontSize: 13 }}>Aucune donnée</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function AlertsFeed({ deliveries, loading }: { deliveries: Delivery[]; loading: boolean }) {
  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 12, padding: '20px 24px',
    border: '1px solid #f3f4f6',
  }

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 600, color: '#0D1117', marginBottom: 16 }}>Alertes récentes</div>
      {loading ? <SkeletonBlock height={140} /> : (
        deliveries.length === 0 ? (
          <div style={{ color: '#9CA3AF', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>
            Aucune alerte
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {deliveries.map((d) => (
              <div key={d._id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 12px', borderRadius: 8,
                background: d.status === 'failed' ? '#FEF2F2' : '#F5F3FF',
                border: `1px solid ${d.status === 'failed' ? '#FECACA' : '#DDD6FE'}`,
              }}>
                <span style={{ fontSize: 16 }}>{d.status === 'failed' ? '❌' : '📵'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: '#0D1117' }}>{d.trackingCode}</div>
                  <div style={{ fontSize: 11, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.dropoff?.contactName} · {d.dropoff?.address}
                  </div>
                </div>
                <div style={{ fontSize: 10, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                  {fmt(d.createdAt)}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  )
}

function AdminDeliveriesTable({ deliveries }: { deliveries: Delivery[] }) {
  if (!deliveries.length) {
    return <div style={{ color: '#9CA3AF', fontSize: 13, padding: '20px 0' }}>Aucun colis récent</div>
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #F3F4F6' }}>
            {['CODE', 'DESTINATAIRE', 'STATUT', 'MONTANT', 'DATE'].map((h) => (
              <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#6B7280', fontSize: 11, letterSpacing: '0.04em' }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {deliveries.map((d) => {
            const color = STATUS_COLOR[d.status] ?? '#9CA3AF'
            return (
              <tr key={d._id} style={{ borderBottom: '1px solid #F9FAFB' }}>
                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0D1117' }}>{d.trackingCode}</td>
                <td style={{ padding: '10px 12px', color: '#374151' }}>
                  <div>{d.dropoff?.contactName}</div>
                  <div style={{ fontSize: 11, color: '#9CA3AF' }}>{d.dropoff?.address?.slice(0, 30)}</div>
                </td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{
                    background: color + '1A', color, borderRadius: 20,
                    padding: '3px 8px', fontSize: 11, fontWeight: 600,
                  }}>
                    {STATUS_LABELS[d.status] ?? d.status}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0D1117' }}>
                  {d.price?.base} TND
                </td>
                <td style={{ padding: '10px 12px', color: '#9CA3AF' }}>{fmtDate(d.createdAt)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const navigate = useNavigate()
  const [period, setPeriod] = useState<StatsPeriod>('month')

  const { data: stats, isLoading: statsLoading } = useAdminStats(period)
  const { data: timelineData, isLoading: timelineLoading } = useAdminTimeline('week')
  const { data: recent, isLoading: recentLoading } = useDeliveries({ limit: 10 })
  const { data: alerts, isLoading: alertsLoading } = useDeliveries({ status: 'failed,unreachable', limit: 5 })

  const headerStyle: React.CSSProperties = {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: 24, flexWrap: 'wrap', gap: 12,
  }

  const sectionStyle: React.CSSProperties = { marginBottom: 20 }

  const cardStyle: React.CSSProperties = {
    background: '#fff', borderRadius: 12, padding: '20px 24px', border: '1px solid #f3f4f6',
  }

  return (
    <div>
      {/* Header */}
      <header style={headerStyle}>
        <div>
          <h1 style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 22, color: '#0D1117', margin: 0 }}>
            Vue d'ensemble
          </h1>
          <p style={{ color: '#9CA3AF', fontSize: 13, marginTop: 4 }}>
            Activité globale TariqGo · {PERIOD_LABELS[period]}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <PeriodSelector value={period} onChange={setPeriod} />
          <NotificationBell />
        </div>
      </header>

      {/* KPI cards */}
      <section className="stat-grid stat-grid-3" style={{ ...sectionStyle, gap: 14 }}>
        <KpiCard
          label="Total colis"
          value={stats?.deliveries.total ?? 0}
          sub={`Taux succès : ${stats?.deliveries.successRate ?? 0}%`}
          icon="📦"
          color="#1D9E75"
          loading={statsLoading}
        />
        <KpiCard
          label="Livrés"
          value={stats?.deliveries.delivered ?? 0}
          sub={`Échecs : ${stats?.deliveries.failed ?? 0}`}
          icon="✅"
          color="#10B981"
          loading={statsLoading}
        />
        <KpiCard
          label="En cours"
          value={stats?.deliveries.inTransit ?? 0}
          sub={`En attente : ${stats?.deliveries.pending ?? 0}`}
          icon="🚗"
          color="#3B82F6"
          loading={statsLoading}
        />
        <KpiCard
          label="Revenus TariqGo"
          value={`${stats?.revenue.commission ?? 0} TND`}
          sub={`Total facturé : ${stats?.revenue.total ?? 0} TND`}
          icon="💰"
          color="#F59E0B"
          loading={statsLoading}
        />
        <KpiCard
          label="Livreurs en ligne"
          value={`${stats?.livreurs.online ?? 0} / ${stats?.livreurs.total ?? 0}`}
          sub="Disponibles maintenant"
          icon="🛵"
          color="#8B5CF6"
          loading={statsLoading}
        />
        <KpiCard
          label="Expéditeurs actifs"
          value={stats?.expediteurs.total ?? 0}
          sub="Comptes enregistrés"
          icon="🏢"
          color="#06B6D4"
          loading={statsLoading}
        />
      </section>

      {/* Timeline + Status */}
      <section className="detail-grid" style={{ ...sectionStyle, '--detail-cols': '2fr 1fr', gap: 14 } as React.CSSProperties}>
        <DeliveryTimelineChart data={timelineData?.timeline ?? []} loading={timelineLoading} />
        <StatusDistribution stats={stats?.deliveries} loading={statsLoading} />
      </section>

      {/* Top Performers + Alerts */}
      <section className="detail-grid" style={{ ...sectionStyle, '--detail-cols': '1fr 1fr', gap: 14 } as React.CSSProperties}>
        <TopPerformersCard stats={stats} loading={statsLoading} />
        <AlertsFeed deliveries={alerts?.data ?? []} loading={alertsLoading} />
      </section>

      {/* Recent deliveries */}
      <section style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#0D1117' }}>Derniers colis</div>
          <button
            onClick={() => navigate('/admin/livraisons')}
            style={{ background: 'none', border: 'none', color: '#1D9E75', fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
          >
            Voir tout →
          </button>
        </div>
        {recentLoading
          ? <SkeletonBlock height={200} />
          : <AdminDeliveriesTable deliveries={recent?.data ?? []} />
        }
      </section>
    </div>
  )
}
